import { useMemo } from 'react';
import { Monitor, Maximize2, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatVNDShort } from '@/lib/format';
import type { ReportMonitor } from '@/lib/api';

export interface ReportSpecBreakdownProps {
  monitors: ReportMonitor[];
}

interface BreakdownRow {
  label: string;
  skuCount: number;
  units: number;
  revenue: number;
  share: number;
}

/** Bucket size into customer-friendly groups (24" / 27" / 32" / etc.) */
function sizeBucket(size: number | null): string {
  if (size == null) return '—';
  if (size < 24) return '< 24"';
  if (size <= 24.5) return '24"';
  if (size <= 25.5) return '25"';
  if (size <= 27.5) return '27"';
  if (size <= 32.5) return '32"';
  if (size <= 34.5) return '34"';
  return '≥ 38"';
}

/** Bucket refresh rate into use-case groups */
function hzBucket(hz: number | null): string {
  if (hz == null) return '—';
  if (hz <= 75) return '≤ 75Hz';
  if (hz <= 144) return '100–144Hz';
  if (hz <= 240) return '165–240Hz';
  return '> 240Hz';
}

function group<T>(items: T[], keyFn: (x: T) => string, valFn: (x: T) => { units: number; revenue: number }) {
  const buckets = new Map<string, { skus: Set<unknown>; units: number; revenue: number }>();
  let totalRevenue = 0;
  for (const it of items) {
    const k = keyFn(it);
    const v = valFn(it);
    totalRevenue += v.revenue;
    const cur = buckets.get(k) ?? { skus: new Set(), units: 0, revenue: 0 };
    cur.skus.add(it);
    cur.units += v.units;
    cur.revenue += v.revenue;
    buckets.set(k, cur);
  }
  const rows: BreakdownRow[] = Array.from(buckets.entries())
    .map(([label, b]) => ({
      label,
      skuCount: b.skus.size,
      units: b.units,
      revenue: b.revenue,
      share: totalRevenue > 0 ? (b.revenue / totalRevenue) * 100 : 0,
    }))
    .filter((r) => r.units > 0 || r.revenue > 0);
  rows.sort((a, b) => b.revenue - a.revenue);
  return rows;
}

function SpecCard({
  title,
  icon: Icon,
  rows,
}: {
  title: string;
  icon: LucideIcon;
  rows: BreakdownRow[];
}) {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold">
        <Icon className="h-3.5 w-3.5 text-primary" />
        <span>{title}</span>
        <span className="text-muted-foreground">({rows.length})</span>
      </div>
      {rows.length === 0 ? (
        <div className="py-2 text-[11px] text-muted-foreground">Không có data</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.label} className="space-y-1">
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className="font-medium">{r.label}</span>
                <span className="tabular-nums text-muted-foreground">
                  {r.skuCount} SKU · {r.units.toLocaleString('vi-VN')} units
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-primary/70"
                    style={{ width: `${Math.min(100, r.share)}%` }}
                  />
                </div>
                <span className="w-14 shrink-0 text-right text-[10px] tabular-nums font-semibold">
                  {r.share.toFixed(1)}%
                </span>
                <span className="w-12 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground">
                  {formatVNDShort(r.revenue)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReportSpecBreakdown({ monitors }: ReportSpecBreakdownProps) {
  const valueOf = (m: ReportMonitor) => ({ units: m.sales_units, revenue: m.sales_revenue });

  const sizeRows = useMemo(() => group(monitors, (m) => sizeBucket(m.size), valueOf), [monitors]);
  const resRows = useMemo(
    () => group(monitors, (m) => m.resolution ?? '—', valueOf),
    [monitors]
  );
  const hzRows = useMemo(
    () => group(monitors, (m) => hzBucket(m.refresh_rate), valueOf),
    [monitors]
  );

  return (
    <div className={cn('grid gap-3 md:grid-cols-3')}>
      <SpecCard title="Theo Kích thước" icon={Maximize2} rows={sizeRows} />
      <SpecCard title="Theo Độ phân giải" icon={Monitor} rows={resRows} />
      <SpecCard title="Theo Tần số quét" icon={Zap} rows={hzRows} />
    </div>
  );
}
